import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, skipWhile, take, tap } from 'rxjs/operators';
import { BehaviorSubject, from, Observable, throwError as observableThrowError } from 'rxjs';
import { FirebaseService } from './firebase.service';
import firebase from 'firebase/app';
import { User } from '../api/user/user';
import { UserRoleType } from '../api/user/user-role-type';
import { UserType } from '../api/user/user-type';
import { sortBy } from '../utils/sort-by';
import { UserGroup } from '../api/organization/user-group';
import { isNullOrUndefinedOrBlank } from '../utils/is-null-or-undefined';
import { isNull } from '../utils/is-null';

/**
 * Firebase hands back every enrolled factor, and the portal has only ever used the first.
 * Named so the choice is visible in the two places that depend on it agreeing.
 */
const MFA_HINT_INDEX = 0;

/**
 * The masked number to show for a multi-factor challenge.
 *
 * The first hint is the factor the challenge will actually go to - sendMfaChallengeCode
 * passes `resolver.hints[0]` to Firebase - so anything shown to the user has to be read from
 * the same index. It was being picked out in each surface separately, which meant the same
 * decision lived in four places (Task 47297).
 *
 * A free function so AuthServiceMock can delegate rather than keep a second copy, as it does
 * for mapSignInErrorMessage.
 */
export function phoneHintFrom(resolver: any): string {
  return resolver?.hints?.[MFA_HINT_INDEX]?.phoneNumber;
}

/**
 * Maps a rejection raised by a sign-in itself - a password check, or an invite link's token
 * exchange - rather than by anything to do with a second factor.
 *
 * Separate from mapMfaError because that throttle copy is written for a case which has not
 * arisen yet here: it says "verification attempts" and offers an administrator's two-factor
 * reset, and neither is true when Firebase refuses the sign-in before a factor is ever
 * reached. Reporting it as bad credentials is worse still - it sends a blocked user to
 * retry, which is the one thing that extends the block.
 *
 * It names no duration, because Firebase publishes none, and no locus, for the reason
 * mapMfaError's own throttle case gives: the target is a key as much as the device is, so
 * switching devices is not a workaround. `retryAdvice` is the caller's, because the way
 * back differs by screen even though the diagnosis does not.
 *
 * A free function rather than a method so AuthServiceMock can delegate to it. The surfaces
 * are tested through that mock, and a second copy of this wording in it would let the two
 * drift while the component tests stayed green (Bug 47869).
 */
export function mapSignInErrorMessage(e: any, fallbackMessage: string, retryAdvice: string): string {
  if (e?.code !== 'auth/too-many-requests') {
    return fallbackMessage;
  }
  return 'Too many sign-in attempts. Automated abuse protection has blocked further attempts. '
    + `There is no fixed wait, but it does clear on its own. ${retryAdvice}`;
}

@Injectable()
export class AuthService {

  /**
   * Currently logged in user.
   * This will always have a value when user is logged in inside the main module.
   */
  public currentUser: User;

  public currentUserType: UserType;

  /**
   * Contains the stream of the currently logged in user.
   *
   * IMPORTANT NOTE:
   * Subscribe to this only when you are implementing a logic that displays or handles the user
   * as it changes in session (e.g top navbar).
   * Also this can return NULL during the app's lifecycle so make sure to handle this.
   *
   * You won't need to subscribe to this 95% of the time.
   * Otherwise - user this.currentUser
   */
  public status = new BehaviorSubject<User>(undefined);

  /**
   * Contains the currently active RAW session token
   * Does not contain proper auth headers that are required for http calls.
   * Use the correct method for getting that one
   */
  public token = null;

  /**
   * Contains the firebase user object for this session.
   * NOTE: Will always have a value when inside the main module.
   */
  private firebaseUser: firebase.User = null;

  /**
   * Only used for debugging until backend roles are full available.
   */
  private forceUserType: UserRoleType;

  private forceRoleStorageKey = 'parkeval_forcerole';

  private verifyOnly = false;

  constructor(private httpClient: HttpClient,
              private firebaseService: FirebaseService) {
    this.listenForSessionChange();

    this.forceUserType = window.localStorage.getItem(this.forceRoleStorageKey) as UserRoleType;
  }

  public login(email: string, password: string, forceRole?: UserRoleType): Observable<firebase.auth.UserCredential> {
    // window.localStorage.setItem(this.forceRoleStorageKey, forceRole);
    this.forceUserType = forceRole;
    return from(this.firebaseService.auth.signInWithEmailAndPassword(email, password));
  }

  /**
   * True when a sign-in attempt was rejected because the account has a second factor
   * enrolled. The error carries a `resolver` used to complete the multi-factor challenge.
   */
  public isMfaRequired(error: any): boolean {
    return error?.code === 'auth/multi-factor-auth-required';
  }

  /**
   * Creates the (invisible) reCAPTCHA verifier Firebase requires before it will send an
   * SMS code. The container must exist in the DOM for the verifier's lifetime. Callers
   * should pass a freshly created child element rather than a reused one — grecaptcha
   * refuses to render twice into the same element after a failed render.
   */
  public createRecaptchaVerifier(container: string | HTMLElement): firebase.auth.RecaptchaVerifier {
    return new (this.firebaseService.fb().auth.RecaptchaVerifier)(container, { size: 'invisible' });
  }

  /**
   * Releases a verifier's grecaptcha widget. Safe on a verifier that was never rendered or
   * has already been cleared.
   */
  public clearRecaptchaVerifier(verifier: firebase.auth.RecaptchaVerifier): void {
    if (!verifier) {
      return;
    }
    try {
      verifier.clear();
    } catch (e) {
      // Already cleared or never rendered - nothing to release.
    }
  }

  /**
   * grecaptcha refuses to render twice into the same element — even after clear() — when a
   * previous render failed mid-flight ("reCAPTCHA has already been rendered in this
   * element"), so every verifier gets a brand-new child element inside `host`. Pass the
   * verifier being replaced so its widget is released before the host is emptied.
   */
  public createFreshRecaptchaVerifier(host: HTMLElement,
                                      previous?: firebase.auth.RecaptchaVerifier): firebase.auth.RecaptchaVerifier {
    this.clearRecaptchaVerifier(previous);
    host.innerHTML = '';
    const slot = document.createElement('div');
    host.appendChild(slot);
    return this.createRecaptchaVerifier(slot);
  }

  /**
   * Re-proves the signed-in user's password. Firebase demands this before anything it treats
   * as credential-sensitive - changing a password, enrolling a second factor, removing one -
   * and rejects it with multi-factor-auth-required when the account already has a factor,
   * which is isMfaRequired's cue below.
   *
   * Here rather than on FirebaseService, beside the rest of the flow it starts: the rejection
   * it raises is handled by isMfaRequired, sendMfaChallengeCode and mapMfaError, all of which
   * live on this class. A sixth credential-sensitive surface should find the whole step-up in
   * one place - the two copies this replaces are how Bug 47241 reached production (Task 47297).
   */
  public reauthenticate(password: string): Promise<unknown> {
    const user = this.firebaseService.auth.currentUser;
    return user.reauthenticateWithCredential(
      this.firebaseService.fb()
        .auth
        .EmailAuthProvider
        .credential(user.email, password));
  }

  /**
   * Sends the SMS challenge code to the first phone factor enrolled on the account.
   * Resolves with the verification id needed by resolveMfaSignIn().
   */
  public sendMfaChallengeCode(resolver: firebase.auth.MultiFactorResolver,
                              recaptchaVerifier: firebase.auth.RecaptchaVerifier): Promise<string> {
    const phoneAuthProvider = new (this.firebaseService.fb().auth.PhoneAuthProvider)();
    return phoneAuthProvider.verifyPhoneNumber(
      {
        multiFactorHint: resolver.hints[MFA_HINT_INDEX],
        session: resolver.session
      },
      recaptchaVerifier);
  }

  /**
   * Builds the phone multi-factor assertion Firebase requires to prove possession of the
   * SMS code — used both to complete an MFA sign-in and to enroll a new second factor.
   */
  public createPhoneAssertion(verificationId: string,
                              verificationCode: string): firebase.auth.PhoneMultiFactorAssertion {
    const fbAuth = this.firebaseService.fb().auth;
    const credential = fbAuth.PhoneAuthProvider.credential(verificationId, verificationCode);
    return fbAuth.PhoneMultiFactorGenerator.assertion(credential);
  }

  /**
   * Completes a multi-factor sign-in with the SMS code the user received. On success the
   * normal onIdTokenChanged session bootstrap runs, exactly as for a single-factor login.
   */
  public resolveMfaSignIn(resolver: firebase.auth.MultiFactorResolver,
                          verificationId: string,
                          verificationCode: string): Promise<firebase.auth.UserCredential> {
    return resolver.resolveSignIn(this.createPhoneAssertion(verificationId, verificationCode));
  }

  /**
   * Maps Firebase MFA/auth error codes to user-facing messages. Shared by the login
   * challenge and the profile panel so the two surfaces cannot drift apart on the same
   * error codes. `fallbackMessage` (with the raw code appended for diagnosability) covers
   * any code without a specific mapping here.
   *
   * `adminResetHelps` says whether an administrator's two-factor reset would get this user
   * moving again: true wherever an enrolled factor is what stands in the way (sign-in, the
   * invite link, a removal or password-change reauthentication), false while enrolling a
   * first factor, where there is nothing enrolled for a reset to clear.
   */
  public mapMfaError(e: any, fallbackMessage: string, adminResetHelps = true): string {
    switch (e?.code) {
      case 'auth/wrong-password':
        return 'Incorrect password. Please check it and try again.';
      // Firebase refuses enrolment until the account's email is verified. The portal never
      // sends a verification email, so there is no link for the user to go and find: the
      // flag is set from isAccountEnabled() when the Firebase account is created and, since
      // Bug 47813, again when an account is activated - but neither reaches an account
      // activated before that shipped, or one whose Firebase record was written by another
      // route. The lever is PUT /user/{userId}/toggle-email-verified.
      //
      // The message names support rather than "your administrator", and does not suggest the
      // user can resolve it themselves, because neither can: only internal staff can run
      // that endpoint. Support now has an interface for it, on the internal user form
      // (Bug 47867) - the endpoint was left without one until Bug 47850 gave it
      // @IsInternalAdmin, since before that any active account could call it for any userId.
      //
      // The copy stays as it is now that the tool exists: "contact support" was always an
      // instruction the user could follow, and the gap this closes was on support's side,
      // not theirs.
      case 'auth/unverified-email':
        return 'Your email address has not been verified, which is required before two-factor authentication can be set up. This has to be corrected on our side, so please contact support.';
      case 'auth/invalid-phone-number':
        return 'Invalid phone number. Include the country code, e.g. +1 555 123 4567.';
      // A blank or whitespace-only code trims to empty; Firebase rejects that client-side
      // rather than reaching the server that would otherwise return invalid-verification-code,
      // so it is mapped to the same message here.
      case 'auth/invalid-verification-code':
      case 'auth/missing-verification-code':
      case 'auth/missing-verification-id':
        return 'Invalid verification code. Please check the text message and re-enter the code.';
      case 'auth/code-expired':
        return 'This code has expired. Click Resend Code to get a new one.';
      case 'auth/second-factor-already-in-use':
        return 'This phone number is already enrolled. Use a different number, or cancel if it is already set up.';
      // Firebase's own anti-abuse throttle, keyed on the requesting device and the target
      // phone number rather than on the account - nothing here or in the portal tracks or
      // clears it, and it has no published duration. In Bug 47480 it held from Aug 31 to
      // Sep 4. So: name no duration at all (every guess so far has been an order of
      // magnitude short) and name no locus either, since the phone number is a key as much
      // as the device is and switching devices is therefore not a workaround.
      //
      // The admin reset (DELETE /user/{userId}/two-factor) does not clear the throttle
      // either - it unenrolls the factor, which is an escape only for a user the factor is
      // locking out, who can then sign in with a password alone. A user enrolling a first
      // factor has nothing enrolled, so that advice sends them to an administrator who can
      // do nothing for them: two accounts blocked mid-enrolment on Test were found with
      // zero enrollments on 2026-09-10.
      case 'auth/too-many-requests':
        return adminResetHelps
          ? 'Too many verification attempts. Automated abuse protection has blocked further requests - there is no fixed wait, but it does clear on its own. If you need access sooner, ask your administrator to reset two-factor authentication on your account.'
          : 'Too many verification attempts. Automated abuse protection has blocked further requests - there is no fixed wait, but it does clear on its own. Your account is unaffected and there is nothing an administrator can reset, so please try setting up two-factor authentication again later.';
      // Raised when the environment's Firebase project has no SMS multi-factor: Identity
      // Platform not upgraded, the sign-in method not enabled, or billing not linked. That
      // is ours to fix, not the client administrator's, so the copy names support (Bug
      // 47814).
      case 'auth/operation-not-allowed':
        return 'Two-factor authentication is not available in this environment. Please contact support.';
      // Reverting or removing a factor revokes the account's sessions; a stale session
      // attempting to enroll or unenroll lands here.
      case 'auth/user-token-expired':
      case 'auth/null-user':
        return 'Your session has expired. Please log out and log in again, then retry.';
      case 'auth/requires-recent-login':
        return 'Your login is too old for this change. Please log out and log in again, then retry.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/captcha-check-failed':
      case 'auth/invalid-app-credential':
        return 'Security verification failed. Please refresh the page and try again.';
      case 'auth/internal-error':
        // Firebase wraps some backend rejections in internal-error with the real cause only
        // present in the message body: BILLING_NOT_ENABLED for missing SMS billing, and
        // INVALID_LOGIN_CREDENTIALS for a failed reauthentication (newer Identity Platform
        // projects report it this way instead of auth/wrong-password).
        if (e?.message?.includes('INVALID_LOGIN_CREDENTIALS')) {
          return 'Incorrect password. Please check it and try again.';
        }
        if (e?.message?.includes('BILLING_NOT_ENABLED')) {
          return 'Text messaging is not enabled for this environment. Please contact your administrator.';
        }
        return this.appendErrorCode(fallbackMessage, e);
      default:
        return this.appendErrorCode(fallbackMessage, e);
    }
  }

  /** See phoneHintFrom - this is the injectable seam onto it. */
  public phoneHint(resolver: any): string {
    return phoneHintFrom(resolver);
  }

  /** See mapSignInErrorMessage - this is the injectable seam onto it. */
  public mapSignInError(e: any, fallbackMessage: string, retryAdvice: string): string {
    return mapSignInErrorMessage(e, fallbackMessage, retryAdvice);
  }

  /**
   * Last-resort message. Includes the Firebase error code so a screenshot or a support
   * ticket is diagnosable without needing the browser console.
   */
  private appendErrorCode(message: string, e: any): string {
    return e?.code ? `${message} (${e.code})` : message;
  }

  public loginToVerify(email: string, password: string): Observable<firebase.auth.UserCredential> {
    // Make sure this is set.
    // We will set this to false once it the session change gets triggered.
    this.verifyOnly = true;
    return from(this.firebaseService.auth.signInWithEmailAndPassword(email, password))
      .pipe(
        catchError((e) => {
          // Set to false again if error.
          this.verifyOnly = false;
          return observableThrowError(e);
        })
      );
  }

  public logout(): Observable<void> {
    return from(this.firebaseService.auth.signOut())
      .pipe(
        tap(() => {
          this.cleanup();
        })
      );
  }

  public hasValidSession(): Observable<boolean> {
    // NOTE:
    // When the value is undefined, it means that the system has not yet received any data about the session
    // When the value is null -  we got the session information but there was no session
    // Value should contain the session user from firebase
    return this.status
      .pipe(
        skipWhile((value) => value === undefined),
        take(1),
        map((value) => {
          return isNull(value) ? false : true;
        })
      );
      // .toPromise();
  }


  public getAuthorizationHeader(): string {
    return isNullOrUndefinedOrBlank(this.token) ? null : 'Bearer ' + this.token;
  }

  public refreshToken(): Observable<string> {
    if (isNullOrUndefinedOrBlank(this.currentUser)) {
      return observableThrowError('Could not refresh token - No active session yet');
    }

    return from(this.firebaseService.auth.currentUser.getIdToken(true))
      .pipe(
        tap(token => {
          // Debug stuff here -
          this.token = token;
        })
      );
  }

  public isInternal(): boolean {
    return !!this.currentUser?.roles.find((role) => role.name === UserRoleType.Internal);
  }

  public isInternalAdmin(): boolean {
    return !!this.currentUser?.roles.find((role) => role.name === UserRoleType.InternalAdmin);
  }

  public isExpert(): boolean {
    return !!this.currentUser?.roles.find((role) => role.name === UserRoleType.Expert);
  }

  public isCompanyAdmin(): boolean {
    return !!this.currentUser?.roles.find((role) => role.name === UserRoleType.CompanyAdmin);
  }

  public isCompany(): boolean {
    return !!this.currentUser?.roles.find((role) => role.name === UserRoleType.Company);
  }

  /**
   * Use this to debug the token-retry interceptor when it's not working.
   * This allows the the interceptor the retry-request procedure.
   */
  public poisonToken(): void {
    this.token = 'Zaf231Z-B.123456';
  }

  /**
   * True once a Firebase session exists, even if the /api/user bootstrap call
   * hasn't resolved yet. Unlike `currentUser`, this is only false after an actual
   * sign-out (cleanup()), so it correctly distinguishes "logged out elsewhere"
   * from "still bootstrapping".
   */
  public hasActiveFirebaseSession(): boolean {
    return !!this.firebaseUser;
  }

  private listenForSessionChange(): void {
    // We can only choose on or the other.
    // Avoid using both onAuthStateChanged and onIdTokenChange at the same time.

    // this.firebaseService
    //   .auth
    //   .onAuthStateChanged((user) => {
    //     console.log('session change', user);
    //     this.syncObjects(user);
    //   });

    this.firebaseService
      .auth
      .onIdTokenChanged((user) => {
        // We need to ignore any changes to auth when we are verifying only.
        // But also make sure we reset it back. This is supposed to be a one-shot thing.
        if (this.verifyOnly) {
          this.verifyOnly = false;
          this.logout();
          return;
        }

        this.status.next(undefined);
        this.syncObjects(user);
      });
  }

  private syncObjects(user: firebase.User): void {
    if (!user) {
      this.cleanup();
      return;
    }

    this.firebaseUser = user;

    user.getIdToken()
      .then((token) => {
        this.token = token;
        this.fetchCurrentUser();
      })
      .catch(() => {
        console.error('Failed to get token.');
        this.cleanup();
      });
  }

  private cleanup(): void {
    this.token = null;
    this.currentUser = null;
    this.firebaseUser = null;
    this.status.next(null);

    try {
      window.localStorage.removeItem('park_est_notice');
      // Other to clear here
      // Probably need a more cleaner way to do this in the future.
    } catch(e) {
      console.warn('Could not clear storage items');
    }
  }

  private fetchCurrentUser(): void {
    this.getUser()
      .subscribe((user) => {
        this.currentUser = user;
        this.currentUser.groups = sortBy(this.currentUser.groups, (group: UserGroup) => group.groupName.toUpperCase());
        this.status.next(user);

        // Use this to debug expert user role
        if (this.forceUserType && this.forceUserType === UserRoleType.Expert) {
          this.forceExpertUser();
        }

        this.parseUserType();

        // Some debugging log
        // console.log('internal:', this.isInternal());
        // console.log('expert:', this.isExpert());
        // console.log(this.currentUserType);

      }, (err) => {

        // TODO: Add handling here depending in the HTTP error type.
        // E.g, when user is disabled it will get 403, etc, redirect to the necessary pages.
        console.error(err);
        this.currentUser = null;
        this.status.next(null);
      });
  }

  /**
   * Fetches the currently logged in user's information
   */
  public getUser(): Observable<User> {
    return this.httpClient.get<User>('/api/user');
  }

  private parseUserType(): void {
    this.currentUserType = null;

    // TODO: jaa-auth remove me, Use to to force client user type for eval > company type
/*    this.currentUserType = UserType.Company;
    return;*/

    if (this.isInternal()) {
      this.currentUserType = UserType.Internal;
      return;
    }

    if (this.isExpert()) {
      this.currentUserType = UserType.Expert;
      return;
    }

    if (this.isCompany()) {
      this.currentUserType = UserType.Company;
      return;
    }
  }

  /**
   * FOR DEBUGGING ONLY.
   * Force the user to be an expert.
   */
  private forceExpertUser(): void {
    this.currentUser.roles = [
      {id: 123, name: UserRoleType.Expert, deleteInd: false}
    ];
  }
}
